import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Pill, Progress } from '../components/Primitives';
import { Icon } from '../components/Icon';
import { PlatformAccess } from '../components/PlatformAccess';
import { useApp } from '../lib/app-context';
import { curriculumProgress, currentReviewDebt, dueWords, dynamicLesson, interfaceMix, knowledgeHealth } from '../lib/learning';
import { calendarSnapshot } from '../lib/calendar';
import type { AppRoute } from '../types';


function MotionNumber({ value, suffix = '', format = true }: { value: number; suffix?: string; format?: boolean }) {
  const previous = useRef(0);
  const [shown, setShown] = useState(0);

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const from = previous.current;
    previous.current = value;
    if (reduceMotion || from === value) {
      setShown(value);
      return;
    }

    let frame = 0;
    const started = performance.now();
    const duration = 620;
    const tick = (now: number) => {
      const progress = Math.min(1, (now - started) / duration);
      const eased = 1 - Math.pow(1 - progress, 4);
      setShown(Math.round(from + (value - from) * eased));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value]);

  const text = format ? shown.toLocaleString() : String(shown);
  return <>{text}{suffix}</>;
}

type ModuleRowProps = {
  icon: React.ComponentProps<typeof Icon>['name'];
  title: string;
  text: string;
  route: AppRoute;
  onOpen: (route: AppRoute) => void;
  meta?: string;
};

function ModuleRow({ icon, title, text, route, onOpen, meta }: ModuleRowProps) {
  return (
    <button className="module-row" onClick={() => onOpen(route)}>
      <span className="module-row-icon"><Icon name={icon} /></span>
      <span className="module-row-copy">
        <strong>{title}</strong>
        <small>{text}</small>
      </span>
      {meta && <span className="module-row-meta">{meta}</span>}
      <Icon name="arrow" size={18} />
    </button>
  );
}

function DashboardMetric({ icon, label, value, hint, tone }: {
  icon: React.ComponentProps<typeof Icon>['name'];
  label: string;
  value: React.ReactNode;
  hint: string;
  tone: 'blue' | 'green' | 'amber' | 'violet';
}) {
  return <div className={`premium-metric premium-metric-${tone}`}>
    <span className="premium-metric-icon"><Icon name={icon} size={22}/></span>
    <div><span>{label}</span><strong>{value}</strong><small>{hint}</small></div>
  </div>;
}

export function Dashboard() {
  const { state, setRoute, entitlement } = useApp();
  const lesson = dynamicLesson(state);
  const health = knowledgeHealth(state);
  const due = dueWords(state);
  const mix = interfaceMix(state);
  const debt = currentReviewDebt(state);
  const calendar = calendarSnapshot(state);
  const accuracy = state.progress.totalAnswers ? Math.round((state.progress.correctAnswers / state.progress.totalAnswers) * 100) : 0;
  const trackedWords = Object.keys(state.progress.words).length;
  const courseProgress = calendar.courseFinished ? 100 : curriculumProgress(state);
  const completedCalendarDays = Math.min(365, state.progress.dailyCompletionDates.length);

  return <div className="screen dashboard-screen premium-dashboard">
    <div className="screen-head dashboard-head premium-page-head">
      <div>
        <span className="eyebrow">ENGLISH FORGE · BUGUN</span>
        <h1>Salom, {state.profile.name || 'Learner'}!</h1>
        <p>{lesson.goalUz}</p>
      </div>
      <div className="premium-head-aside">
        <Pill tone={entitlement.plan === 'free' ? 'neutral' : 'blue'}>{entitlement.plan === 'trial' ? '7 KUNLIK TRIAL' : entitlement.plan.toUpperCase()}</Pill>
        <span>Day {calendar.day}/365 · {calendar.courseDate}</span>
      </div>
    </div>

    <div className="premium-metric-grid">
      <DashboardMetric icon="book" label="Kalendar kuni" value={<MotionNumber value={calendar.day} suffix="/365"/>} hint={calendar.todayMissionCompleted ? 'Bugungi missiya bajarildi' : 'Bugungi missiya kutilmoqda'} tone="blue"/>
      <DashboardMetric icon="spark" label="Jami XP" value={<MotionNumber value={state.progress.xp}/>} hint={`${state.progress.streak} kunlik streak`} tone="green"/>
      <DashboardMetric icon="repeat" label="Review qarzi" value={<MotionNumber value={debt}/>} hint={`${due.length} ta bugungi takrorlash`} tone="amber"/>
      <DashboardMetric icon="check" label="Aniqlik" value={<MotionNumber value={accuracy} suffix="%"/>} hint={`${state.progress.totalAnswers} ta real javob`} tone="violet"/>
    </div>

    <div className="premium-dashboard-grid">
      <Card className={`daily-hero premium-daily-panel ${calendar.todayMissionCompleted ? 'daily-complete' : ''} ${calendar.courseFinished ? 'maintenance-mode' : ''}`}>
        <div className="daily-hero-copy">
          <div className="daily-top">
            <span>{calendar.courseFinished ? '365 KUNLIK YO‘L YAKUNLANDI' : `DAY ${calendar.day} / 365`}</span>
            <span>{lesson.level}</span>
            {lesson.isBoss && <span>BOSS DAY</span>}
          </div>
          <span className="premium-panel-kicker">BUGUNGI DARS</span>
          <h2>{calendar.courseFinished ? 'Maintenance va fluency' : calendar.todayMissionCompleted ? 'Bugungi missiya bajarildi' : lesson.titleUz}</h2>
          <p>{calendar.courseFinished ? 'Asosiy 365 kunlik yo‘l tugadi. Endi recall, speaking, listening va reading orqali bilimni mustahkamlaysiz.' : calendar.todayMissionCompleted ? 'Completion saqlandi. Qo‘shimcha recall qilishingiz mumkin; Day faqat keyingi kalendar sanasida yangilanadi.' : lesson.grammar}</p>
          <Progress value={courseProgress} label="365 kunlik kalendar yo‘li"/>
          <div className="daily-actions">
            <Button onClick={() => setRoute(calendar.courseFinished ? 'review' : 'lesson')}>
              <Icon name={calendar.courseFinished || calendar.todayMissionCompleted ? 'repeat' : 'play'} size={18}/>
              {calendar.courseFinished ? 'Maintenance mashqi' : calendar.todayMissionCompleted ? 'Qo‘shimcha mashq' : 'Bugungi darsni boshlash'}
            </Button>
            {due.length > 0 && <Button variant="secondary" onClick={() => setRoute('review')}>{due.length} takrorlash</Button>}
          </div>
        </div>
        <div className="premium-day-orb" aria-label={`Kalendar kuni ${calendar.day}`}>
          <div className="premium-day-orb-inner">
            <strong>{calendar.courseFinished ? '∞' : calendar.todayMissionCompleted ? '✓' : calendar.day}</strong>
            <span>{calendar.courseFinished ? 'FLUENCY' : calendar.todayMissionCompleted ? 'DONE' : 'DAY'}</span>
          </div>
        </div>
      </Card>

      <Card className="premium-progress-panel">
        <div className="card-head"><div><span className="eyebrow">BUGUNGI PROGRESS</span><h3>Real o‘qish holati</h3></div><button className="text-button" onClick={() => setRoute('stats')}>Batafsil</button></div>
        <div className="premium-progress-ring" style={{ '--progress': `${courseProgress * 3.6}deg` } as React.CSSProperties}>
          <div><strong>{courseProgress}%</strong><span>365 kunlik yo‘l</span></div>
        </div>
        <div className="premium-progress-list">
          <span><i className="dot dot-blue"/>Bajarilgan real kunlar <b>{completedCalendarDays}</b></span>
          <span><i className="dot dot-green"/>Davomiylik <b>{state.progress.streak}</b></span>
          <span><i className="dot dot-amber"/>Review qarzi <b>{debt}</b></span>
          <span><i className="dot dot-violet"/>Rank <b>{state.progress.rank}</b></span>
        </div>
      </Card>
    </div>

    {calendar.missedDays > 0 && <Card className="recovery-banner premium-recovery-banner">
      <span className="premium-recovery-icon"><Icon name="repeat"/></span>
      <div>
        <strong>{calendar.missedDays} kunlik recovery qarzi mavjud</strong>
        <span>Day orqaga qaytmaydi. O‘tkazib yuborilgan bilimlar Memory Forge va Daily Mission ichida qayta taqsimlanadi.</span>
      </div>
      <Button variant="secondary" onClick={() => setRoute('review')}>Recovery</Button>
    </Card>}

    <div className="dashboard-clusters premium-cluster-grid">
      <Card className="module-cluster">
        <div className="cluster-head">
          <div><span className="eyebrow">O‘RGANISH</span><h3>Asosiy yo‘l</h3></div>
          <span>{trackedWords} so‘z kuzatuvda</span>
        </div>
        <div className="module-list">
          <ModuleRow icon="cards" title="So‘zlar" text="Recall, meaning va production evidence" meta={`${trackedWords}`} route="vocabulary" onOpen={setRoute}/>
          <ModuleRow icon="grammar" title="Grammatika" text={lesson.grammar} route="grammar" onOpen={setRoute}/>
          <ModuleRow icon="headphones" title="Ko‘nikmalar" text="Tinglash · O‘qish · Talaffuz" route="skills" onOpen={setRoute}/>
        </div>
      </Card>

      <Card className="module-cluster">
        <div className="cluster-head">
          <div><span className="eyebrow">MUSTAHKAMLASH</span><h3>Xotira va recovery</h3></div>
          <span>{debt} review qarzi</span>
        </div>
        <div className="module-list">
          <ModuleRow icon="repeat" title="Takrorlash" text="Bugungi due va weak bilimlarni qayta chaqirish" meta={`${due.length}`} route="review" onOpen={setRoute}/>
          <ModuleRow icon="trophy" title="Boss Battle" text="Haftalik umumiy bilim sinovi" route="boss" onOpen={setRoute}/>
          <ModuleRow icon="spark" title="Bilim holati" text="Mastery va recovery holatini ko‘rish" route="knowledge" onOpen={setRoute}/>
        </div>
      </Card>
    </div>

    <div className="two-col dashboard-insights premium-insight-grid">
      <Card>
        <div className="card-head"><div><span className="eyebrow">BILIM HOLATI</span><h3>Ko‘nikmalar balansi</h3></div><button className="text-button" onClick={() => setRoute('knowledge')}>Batafsil</button></div>
        <div className="health-list">{Object.entries(health).map(([name, value]) => <div className="health-row" key={name}><span>{name}</span><Progress value={value}/><strong>{value}%</strong></div>)}</div>
      </Card>
      <Card>
        <div className="card-head"><div><span className="eyebrow">MOSLASHUVCHAN TIL</span><h3>O‘zbekcha → Inglizcha</h3></div><Pill tone="blue">Stage {mix.stage}</Pill></div>
        <div className="mix-bars"><div style={{ width: `${mix.uz}%` }} className="mix-uz">UZ {mix.uz}%</div><div style={{ width: `${mix.en}%` }} className="mix-en">EN {mix.en}%</div></div>
        <p className="muted">Interfeys bilim darajangiz va aniqligingiz oshgani sari inglizchaga bosqichma-bosqich o‘tadi.</p>
        <div className="strict-note"><Icon name="clock"/><div><strong>{state.profile.studyTime} — dars vaqti</strong><span>{state.settings.strictTeacher ? 'Qattiqqo‘l ustoz rejimi faol' : 'Yumshoq eslatmalar'}</span></div></div>
      </Card>
    </div>

    <PlatformAccess/>
  </div>;
}
